import * as THREE from "three";

import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { MeshSurfaceSampler } from 'three/examples/jsm/math/MeshSurfaceSampler';

import Garden from '../../Garden';
import Bloom from '../Bloom';

import ShootMaterial from "../ShootMaterial";
import ShootGeometry from "../ShootGeometry";

import { Animator, Easing } from "../Animation"

import { start, Sampler } from 'tone';

interface Sample {
    origin: THREE.Vector3,
    position: THREE.Vector3,
    normal: THREE.Vector3,
    offset: number
}

interface BoundingSphere {
    sample: Sample,
    radius: number
}

interface GeometryViewPreset {
    modelUrl: string,
    minBloomRadius: number,
    maxBloomRadius: number,
    minSurfaceOffset: number,
    maxSurfaceOffset: number,
    textureScale: number
}

export class GeometryView extends THREE.Group implements GardenView {
    maxBlooms: number = 0;
    bloomCount: number = 0;

    blooms: Array<Bloom> = [];

    samples: Array<Sample> = [];
    spheres: Array<BoundingSphere> = []

    blooming: boolean = false;

    preset: GeometryViewPreset;

    baseMaterial;

    raycaster = new THREE.Raycaster();

    generated = false;

    synth = null;

    animationData = new Map();


    // Spring simulation constants
    springMass = 1;  // Mass
    springConstant = 0.05;  // Spring constant
    springDamping = 0.9; // Damping
    springRest = 1;  // Rest position

    constructor(preset: GeometryViewPreset) {
        super();

        this.preset = preset;

        // let clone;

        new OBJLoader().load(
            preset.modelUrl,
            (model) => {

                model.traverse((child) => {
                    if (child instanceof THREE.Mesh) {

                        if (child.name == "base") {
                            this.generateBase(preset, child);
                        }

                        if (child.name == "sample") {
                            this.generateSamples(preset, child);
                        }

                        let mesh = child;
                    }


                    // mesh = new THREE.Mesh(new THREE.SphereGeometry(), new THREE.MeshBasicMaterial());
                    // let geometry = mesh.geometry;

                    // {
                    //     let colors = [];
                    //     const positionAttribute = geometry.getAttribute("position");
                    //     let zero = new THREE.Vector3(0, 0, 0);
                    //     for (let i = 0; i < positionAttribute.count; i++) {
                    //         let vertex = new THREE.Vector3(positionAttribute.getX(i), positionAttribute.getY(i), positionAttribute.getZ(i));
                    //         let distance = vertex.distanceTo(zero);
                    //         colors.push(distance, distance, distance);
                    //     }
                    //     geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
                    // }


                    // this.materialB = new ShootMaterial({
                    //     uniforms: {
                    //         shootTexture: { value: texture },
                    //         shootUVTransform: { value: new THREE.Vector4(0, 0, preset.textureScale, preset.textureScale) },
                    //         shootTextureWeight: { value: 1 },
                    //         shootTextureAlpha: { value: 0 }
                    //     }
                    // });
                    // this.materialB.transparent = true;

                    // clone = mesh.clone();
                    // clone.position.setZ(.05);
                    // clone.material = this.materialB;
                    // this.add(model);
                    // this.add(clone);
                });
            });
    }

    generateBase(preset, mesh) {

        let colorMesh = mesh.clone();
        // let depthMesh = mesh.clone();

        let texture = new THREE.TextureLoader().load(
            new URL('../../assets/textures/leaves.png', import.meta.url).toString(),
            (texture) => {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
            });

        let uniforms = {
            shootTexture: { value: texture },
            shootUVTransform: { value: new THREE.Vector4(0, 0, preset.textureScale, preset.textureScale) },
            // shootUVTransform: { value: new THREE.Vector4(0, 0, preset.textureScale * .35, preset.textureScale * .35) },
            shootTextureWeight: { value: 1 },
            shootTextureTint: { value: new THREE.Vector3(.5, .5, .5) },
            shootTextureAlpha: { value: 0 },
            lightPosition: { value: new THREE.Vector3(0, 1, 2) },
            progress: { value: 1 }
        };

        this.baseMaterial = new ShootMaterial({ uniforms });
        this.baseMaterial.transparent = true;
        this.baseMaterial.side = THREE.FrontSide;
        colorMesh.material = this.baseMaterial;

        // depthMesh.material = new THREE.MeshBasicMaterial({ colorWrite: false });

        // this.add(depthMesh);
        this.add(colorMesh);
    }

    generateSamples(preset, mesh) {


        const sampler = new MeshSurfaceSampler(mesh).build();

        // Store samples.
        for (let i = 0; i < 512 * 256; i++) {
            const origin = new THREE.Vector3();
            const normal = new THREE.Vector3();
            const position = new THREE.Vector3();

            sampler.sample(origin, normal);

            position.copy(origin);
            let offset = (preset.minSurfaceOffset + Math.random() * (preset.maxSurfaceOffset - preset.minSurfaceOffset));
            position.addScaledVector(normal, offset);

            let domeSample: Sample =
            {
                origin: origin,
                position: position,
                normal: normal,
                offset: offset
            }

            this.samples.push(domeSample);
        }
        console.log("Samples: " + this.samples.length);

        // Initial sphere.
        let initialSphere: BoundingSphere = {
            sample: this.samples.pop(),
            radius: THREE.MathUtils.randFloat(.2, 2)
        }
        this.spheres.push(initialSphere);

        // Calculate sphere.
        this.samples.forEach((sample) => {

            let maxRadius = 2;

            let valid = this.spheres.every(sphere => {
                let distance = sample.position.distanceTo(sphere.sample.position);

                // Discard if contained in target sphere.
                if (distance < sphere.radius && sphere.radius < preset.maxBloomRadius) {
                    return false;
                }

                let newRadius = Math.abs(sphere.radius - distance);

                // Discard if too small.
                if (newRadius < preset.minBloomRadius) {
                    return false;
                }

                // Store new max radius.
                if (newRadius < maxRadius) maxRadius = newRadius;

                return true;
            });

            if (valid) {
                this.spheres.push({
                    sample: sample,
                    radius: maxRadius
                })
            }
        });

        // Sort spheres.
        let zero = new THREE.Vector3(0, 0, 0);
        this.spheres.sort((a, b) => {
            let distanceA = a.sample.position.distanceToSquared(zero);
            let distanceB = b.sample.position.distanceToSquared(zero);
            return distanceB - distanceA;
        });

        console.log("Spheres: " + this.spheres.length);

        // this.spheres.forEach(sphere => {

        //     let geometry = new THREE.SphereBufferGeometry(sphere.radius);
        //     let wireframeGeometry = new THREE.WireframeGeometry(geometry);

        //     const wireframe = new THREE.LineSegments(wireframeGeometry);
        //     if (sphere.radius > this.preset.maxBloomRadius) {
        //         wireframe.material.opacity = 0.15;
        //         wireframe.material.color = new THREE.Color(.5, .5, .5);
        //     } else {
        //         wireframe.material.opacity = 0.25;
        //         wireframe.material.color = new THREE.Color(0, 1, 0);
        //     }

        //     wireframe.material.depthTest = false;
        //     wireframe.material.transparent = true;

        //     wireframe.position.copy(sphere.sample.position);

        //     this.add(wireframe);
        // });

        //this.maxBlooms = 512;

        // {
        //     let geometry = new ShootGeometry({
        //         shootCount: 1,

        //         maxWidth: 1,
        //         maxHeight: 1,

        //         widthSegments: 6,
        //         lengthSegments: 4,

        //         pathFunction() {
        //             let points = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 1, 0)];
        //             return new THREE.CatmullRomCurve3(points);
        //         },

        //         getNormalOffset(lengthProgress, widthProgress) {
        //             return Math.sin(widthProgress * Math.PI) * Math.sqrt(1 - Math.pow((1 - lengthProgress) - 1, 2));
        //         },

        //         getBinormalOffset(lengthProgress, widthProgress) {
        //             return Math.cos(widthProgress * Math.PI) * Math.sqrt(1 - Math.pow((1 - lengthProgress) - 1, 2));
        //         },

        //         transformInstance(i) {
        //             return new THREE.Matrix4();
        //         }
        //     });

        //     let material = new ShootMaterial(
        //         {
        //             shootTextureIndex: 8,
        //             uniforms: {
        //                 shootTextureIndex: { value: 0 },
        //                 shootTextureColorA: { value: new THREE.Color(0.22, 0.71, 0.01) },
        //                 shootTextureColorB: { value: new THREE.Color(0.1, .25, 0.01) },
        //             }
        //         }
        //     );

        //     let instanceMesh = new THREE.InstancedMesh(geometry, material, 1024);
        //     let count = 0;
        //     this.spheres.forEach(sphere => {
        //         if (sphere.radius > this.minBloomRadius, sphere.radius < this.maxBloomRadius) {
        //             let matrix = new THREE.Matrix4();
        //             let quaternion = new THREE.Quaternion();

        //             quaternion.setFromUnitVectors(new Vector3(0, 1, 0), sphere.sample.normal);
        //             matrix.compose(
        //                 sphere.sample.origin,
        //                 quaternion,
        //                 new THREE.Vector3(1, .1, 1).multiplyScalar(sphere.radius)//.setY(sphere.sample.offset)
        //             )

        //             instanceMesh.setMatrixAt(count, matrix);
        //             count++;
        //         }
        //     });
        //     instanceMesh.instanceMatrix.needsUpdate = true;
        //     this.add(instanceMesh);
        // }
    }

    generate() {
        this.blooming = true;

        Animator.animate({
            duration: 5000,
            updateCallback: function (t) {
                t = Easing.EaseOutCubic(t);
                this.baseMaterial.uniforms.shootTextureAlpha.value = t;
                // this.materialB.uniforms.shootTextureAlpha.value = t;

            }.bind(this)
        });
    }

    progress(value) {
        this.baseMaterial.uniforms.shootTextureAlpha.value = value;
    }

    generateBloom() {
        if (this.spheres.length == 0) return;

        let sphere = this.spheres.pop();
        const left = new THREE.Vector3(1, 0, 0);
        const up = new THREE.Vector3(0, 1, 0);

        if (sphere.radius <= this.preset.maxBloomRadius) {
            let preset = Garden.Presets[Math.floor(Math.random() * Garden.Presets.length)];
            let bloom = new Bloom(preset);
            let scale = (1 / preset.bounds) * sphere.radius;

            bloom.position.copy(sphere.sample.position);
            bloom.scale.set(scale, scale, scale);

            bloom.lookAt(new THREE.Vector3().addVectors(sphere.sample.position, sphere.sample.normal));
            bloom.rotateOnAxis(left, Math.PI / 2);
            bloom.rotateOnAxis(up, THREE.MathUtils.randFloat(-1, 1) * Math.PI);

            this.animationData.set(bloom, {
                scale,
                rotation: bloom.rotation.y,
                position: this.springRest,   // Position
                velocity: 0, // Velocity
                acceleration: 0,   // Acceleration
                force: 0,   // Force
            });

            bloom.reset();
            bloom.grow(500 + Math.random() * 1000);

            this.add(bloom);
            this.blooms.push(bloom);
        }
    }

    update(time) {


        this.blooms.forEach((bloom) => {

            let animationData = this.animationData.get(bloom);

            animationData.force = -this.springConstant * (animationData.position - this.springRest); // f=-ky
            animationData.acceleration = animationData.force / this.springMass;          // Set the acceleration, f=ma == a=f/m
            animationData.velocity = this.springDamping * (animationData.velocity + animationData.acceleration);  // Set the velocity
            animationData.position = animationData.position + animationData.velocity;        // Updated position

            // if (Math.abs(animationData.velocity) < .05) animationData.velocity = 0;

            let delta = Math.abs(animationData.position - this.springRest);
            let tint = new THREE.Color(1, 1, 1).addScalar(delta * 2);
            bloom.materials.forEach(material => {
                material.uniforms.shootTextureTint.value = tint;
            });

            bloom.scale.setScalar(animationData.position * animationData.scale);
            bloom.rotateY((this.springRest - animationData.position) * .1);
        });


        if (this.blooming && this.spheres.length > 0) {
            for (let i = 0; i < 5; i++)
                this.generateBloom();
        }

        this.blooms.forEach((bloom) => {
            bloom.update(time);
        });

        Animator.update();
    }

    click(mouse, camera) {

        if (this.synth == null) {
            start();

            let sampleC3Url = new URL('../../assets/audio/c3.mp3', import.meta.url).toString();
            let sampleC4Url = new URL('../../assets/audio/c4.mp3', import.meta.url).toString();
            let sampleC5Url = new URL('../../assets/audio/c5.mp3', import.meta.url).toString();

            this.synth = new Sampler({
                "C3": sampleC3Url,
                "C4": sampleC4Url,
                "C5": sampleC5Url
            }, () => {
                console.log("Synth ready.");
            }).toDestination();
            // this.synth = new Tone.FMSynth({
            //     "harmonicity": 8,
            //     "modulationIndex": 2,
            //     "oscillator": {
            //         "type": "sine"
            //     },
            //     "envelope": {
            //         "attack": 0.001,
            //         "decay": 2,
            //         "sustain": 0.1,
            //         "release": 2
            //     },
            //     "modulation": {
            //         "type": "square"
            //     },
            //     "modulationEnvelope": {
            //         "attack": 0.002,
            //         "decay": 0.2,
            //         "sustain": 0,
            //         "release": 0.2
            //     }
            // }).toDestination();
        }

        if (!this.generated) {
            this.generate();
            this.generated = true;
        }

        this.raycaster.setFromCamera(mouse, camera);
        this.raycaster.layers.set(1);

        // this.blooms.forEach((bloom) => {
        const intersection = this.raycaster.intersectObjects(this.blooms);
        if (intersection.length > 0) {
            let targetBloom = intersection[0].object.parent as Bloom;

            let animationData = this.animationData.get(targetBloom);
            animationData.position = animationData.position * .75;

            // if (animationData.velocity == 0) {
            // animationData.velocity += -.1;
            // } else {
            //     animationData.velocity *= 1.1;
            // }


            let notes = [
                "E2", "G2",
                "C3", "E3", "G3",
                "C4", "E4", "G4",
                "C5", "E5",
            ];
            let note = notes[Math.floor(Math.random() * notes.length)];
            this.synth.triggerAttack(note);

            this.blooms.forEach((bloom) => {
                let distance = targetBloom.position.distanceTo(bloom.position);
                let delay = distance * 500 * THREE.MathUtils.randFloat(.85, 1);

                setTimeout(() => {
                    let animationData = this.animationData.get(bloom);
                    animationData.velocity += -.1 * Math.sign(animationData.velocity + .001);
                }, delay);

                // let startScale = bloom.scale.x * .75;
                // let endScale = bloom.scale.x;

                // let startRotationY = bloom.rotation.y;
                // let randomRotation = THREE.MathUtils.randFloat(-2, 2);
                // let distance = targetBloom.position.distanceToSquared(bloom.position);


                // let animationData = Animator.animate({
                //     delay: distance * 1000,
                //     duration: 2500,
                //     startCallback: function () {
                //         bloom.scale.set(startScale, startScale, startScale);
                //     },
                //     updateCallback: function (t) {
                //         t = Easing.EaseOutCubic(t);

                //         let currentScale = THREE.MathUtils.lerp(startScale, endScale, t);
                //         bloom.scale.setScalar(currentScale);

                //         bloom.materials.forEach(material => {
                //             material.uniforms.shootTextureTint.value = new THREE.Color(1, 1, 1).addScalar(Math.sin(t * Math.PI));
                //         });

                //         bloom.rotation.y = startRotationY + (t * randomRotation);
                //     }.bind(bloom)
                // });

                // bloom.animationData = animationData;
            })
        }
        // });
    }

}
